import { logger } from '@beelday/common'
import {
	loadYouTubeIframeApi,
	YouTubeIframeApi,
	YouTubePlayer,
	youTubePhase,
} from 'features/youtube-streaming/youtube-utils'
import { RefObject, useEffect, useRef } from 'react'
import type { MediaProgress } from 'room/common/media-progress'
import {
	expectedPositionMs,
	monitorCorrection,
	MonitorCommand,
	nextJumpLeadMs,
} from './model/monitor-correction'

const log = logger.create('presenter-mode')

/**
 * How often the monitor looks at the projector's last report, besides at
 * every report that comes in.
 */
export const MONITOR_LOOK_MS = 200
/**
 * After a seek or a jump the player's position takes a moment to be the new
 * one: moving it again before would move it twice.
 */
export const MONITOR_SETTLE_MS = 600
/** Where the first jump lands ahead of the projector, before any is measured. */
export const FIRST_JUMP_LEAD_MS = 300

/**
 * The player's own controls and everything else that would let the trainer
 * work the monitor, instead of the remote, or show anything but the film.
 */
const MONITOR_PLAYER_VARS = {
	controls: 0,
	disablekb: 1,
	fs: 0,
	rel: 0,
	iv_load_policy: 3,
	cc_load_policy: 0,
	modestbranding: 1,
	playsinline: 1,
}

/**
 * The film the projector is playing, played again - silent - in an element of
 * the console, so the trainer sees what the room sees: the same frame, at the
 * same moment. Returns the ref of that element; the player is built into it
 * and follows `remote`, the presentation window's last report, by the rules
 * in `model/monitor-correction.ts` - at every report and every
 * `MONITOR_LOOK_MS` in between. It plays nothing on its own: with no report
 * it stops.
 *
 * Silent because the projector's speakers play the sound, and two sources
 * would echo: the player is muted before it first plays, and stays muted.
 */
export const useVideoMonitor = (
	videoId: string | null,
	remote: MediaProgress | null
): RefObject<HTMLDivElement> => {
	const host = useRef<HTMLDivElement>(null)
	const latest = useRef(remote)
	latest.current = remote
	const lookNow = useRef<() => void>()

	useEffect(() => {
		const element = host.current
		if (!videoId || !element) {
			return
		}
		let cancelled = false
		let player: YouTubePlayer | null = null
		let looking: number | undefined
		let settledAt = 0
		let jumpLeadMs = FIRST_JUMP_LEAD_MS
		let jumped = false

		const apply = (current: YouTubePlayer, command: MonitorCommand): void => {
			switch (command.type) {
				case 'play':
					current.playVideo()
					return
				case 'pause':
					current.pauseVideo()
					return
				case 'seek':
					settledAt = Date.now() + MONITOR_SETTLE_MS
					current.seekTo(command.positionMs / 1000, true)
					return
				case 'jump':
					settledAt = Date.now() + MONITOR_SETTLE_MS
					jumped = true
					current.setPlaybackRate(1)
					current.seekTo(command.positionMs / 1000, true)
					current.playVideo()
					return
				case 'rate':
					current.setPlaybackRate(command.rate)
					return
			}
		}

		const look = (current: YouTubePlayer): void => {
			if (!current.isMuted()) {
				current.mute()
			}
			const now = Date.now()
			const remote = latest.current
			const local = {
				positionMs: current.getCurrentTime() * 1000,
				phase: youTubePhase(current.getPlayerState()),
				rate: current.getPlaybackRate(),
			}
			const settling = now < settledAt
			// The first look at a jump that has landed and plays tells how long
			// the jump waited for its data: the next one lands that much ahead.
			if (jumped && !settling && local.phase === 'playing' && remote?.playing) {
				jumped = false
				jumpLeadMs = nextJumpLeadMs(
					jumpLeadMs,
					local.positionMs - expectedPositionMs(remote, now)
				)
			}
			monitorCorrection({ remote, local, now, settling, jumpLeadMs }).forEach(
				command => apply(current, command)
			)
		}

		loadYouTubeIframeApi().then(
			api => {
				if (cancelled) {
					return
				}
				const yt = api as YouTubeIframeApi
				// The API replaces the element it is given with its frame; React's
				// own element stays put around it.
				const mount = document.createElement('div')
				element.appendChild(mount)
				const created: YouTubePlayer = new yt.Player(mount, {
					videoId,
					playerVars: {
						...MONITOR_PLAYER_VARS,
						origin: window.location.origin,
					},
					events: {
						onReady: () => {
							if (cancelled) {
								return
							}
							created.mute()
							const frame = created.getIframe()
							if (frame) {
								frame.tabIndex = -1
							}
							lookNow.current = () => look(created)
							looking = window.setInterval(() => look(created), MONITOR_LOOK_MS)
						},
					},
				})
				player = created
			},
			error =>
				log.warn('The video monitor could not load the YouTube player', error)
		)

		return () => {
			cancelled = true
			lookNow.current = undefined
			window.clearInterval(looking)
			player?.destroy()
			element.replaceChildren()
		}
	}, [videoId])

	// A report that comes in is looked at at once: a pause on the projector is
	// a pause here within the same moment, not at the next look.
	useEffect(() => lookNow.current?.(), [remote])

	return host
}
